<script setup lang="ts">
import {
  CheckCircle2,
  CheckSquare,
  Layout,
  Clock,
  MessageCircle,
  Sparkles,
  CalendarCheck,
  CreditCard,
  Receipt,
  ShoppingBag,
  Gift,
  Star,
  Megaphone,
  FileText
} from 'lucide-vue-next';
</script>

<template>
  <section id="features" class="py-16 lg:py-24 bg-slate-50 relative overflow-hidden">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
        <h2 class="text-2xl sm:text-4xl md:text-5xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight">האפליקציה שמחזירה לכם את השליטה.</h2>
        <p class="text-base sm:text-xl text-slate-600">
          בנינו את טיימאיט מתוך הבנה עמוקה של היום-יום שלכם. פחות התעסקות במסביב, ויותר זמן להתעסק בלקוחות שלכם (או פשוט לנוח).
        </p>
      </div>

      <div class="space-y-16 lg:space-y-32">

        <!-- Feature 1: Automated Calendar & Smart Filling -->
        <div class="flex flex-col lg:flex-row items-center gap-10 lg:gap-20">
          <div class="flex-1 lg:pr-8 text-center lg:text-right">
            <div class="w-14 h-14 sm:w-16 sm:h-16 bg-pink-100 text-pink-600 rounded-2xl flex items-center justify-center mb-6 sm:mb-8 mx-auto lg:mx-0 shadow-inner">
              <Layout class="w-7 h-7 sm:w-8 sm:h-8" />
            </div>
            <h3 class="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 leading-tight">היומן שלכם מתמלא לבד, גם באמצע הלילה</h3>
            <p class="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8">
              תשכחו מהודעות של "יש לך תור פנוי מחר?". הלקוחות מקבלים קישור עם המיתוג שלכם, רואים מתי אתם פנויים, וקובעים בעצמם. <strong>לקוח ביטל?</strong> המערכת מזהה לקוחות פוטנציאליים או שולחת הודעה לממתינים ברשימת ההמתנה, וממלאת את החלל באופן אוטומטי!
            </p>
            <ul class="space-y-3 sm:space-y-4 text-slate-700 font-medium text-sm sm:text-lg">
              <li class="flex items-start lg:items-center justify-center lg:justify-start gap-2 sm:gap-3 text-right">
                <div class="bg-pink-100 p-1 rounded-full text-pink-600 shrink-0 mt-0.5 lg:mt-0"><CheckCircle2 class="w-4 h-4 sm:w-5 sm:h-5" /></div>
                <span>מילוי יומן חכם ורשימת המתנה אוטומטית</span>
              </li>
              <li class="flex items-start lg:items-center justify-center lg:justify-start gap-2 sm:gap-3 text-right">
                <div class="bg-pink-100 p-1 rounded-full text-pink-600 shrink-0 mt-0.5 lg:mt-0"><CheckCircle2 class="w-4 h-4 sm:w-5 sm:h-5" /></div>
                <span>בלי טעויות אנוש, ובלי קביעה כפולה</span>
              </li>
              <li class="flex items-start lg:items-center justify-center lg:justify-start gap-2 sm:gap-3 text-right">
                <div class="bg-pink-100 p-1 rounded-full text-pink-600 shrink-0 mt-0.5 lg:mt-0"><CheckCircle2 class="w-4 h-4 sm:w-5 sm:h-5" /></div>
                <span>גבולות ברורים, אתם מחליטים מתי בדיוק עובדים</span>
              </li>
            </ul>
          </div>
          <div class="flex-1 w-full relative perspective-1000 mt-6 lg:mt-0">
            <div class="relative bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl shadow-slate-200 border border-slate-100 overflow-hidden transform transition-transform hover:-translate-y-2 duration-500">
              <div class="bg-slate-100 px-3 sm:px-4 py-2 sm:py-3 border-b border-slate-200 flex items-center gap-1.5 sm:gap-2">
                <div class="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-400"></div>
                <div class="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-400"></div>
                <div class="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-400"></div>
                <div class="mx-auto bg-white px-3 sm:px-4 py-1 rounded-md text-[10px] sm:text-xs text-slate-400 font-mono truncate max-w-[150px] sm:max-w-[200px]">timeit.co.il/shiran</div>
              </div>
              <div class="p-5 sm:p-8">
                <div class="flex flex-col items-center mb-6 sm:mb-8">
                  <div class="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-tr from-pink-200 to-purple-200 rounded-full flex items-center justify-center mb-3 sm:mb-4 text-xl sm:text-2xl">💅</div>
                  <h4 class="font-bold text-lg sm:text-xl text-slate-900">שירן ביוטי קליניק</h4>
                  <p class="text-slate-500 text-xs sm:text-sm">קוסמטיקה וטיפוח</p>
                </div>
                <div class="space-y-2 sm:space-y-3">
                  <div class="p-3 sm:p-4 border border-slate-100 rounded-xl sm:rounded-2xl flex justify-between items-center bg-slate-50 hover:border-pink-300 transition-colors cursor-pointer">
                    <div>
                      <p class="font-bold text-slate-800 text-sm sm:text-base">מניקור ג'ל + מבנה</p>
                      <p class="text-xs sm:text-sm text-slate-500">60 דקות</p>
                    </div>
                    <span class="font-bold text-pink-600 text-sm sm:text-base">₪120</span>
                  </div>
                  <div class="p-3 sm:p-4 border-2 border-pink-500 rounded-xl sm:rounded-2xl flex justify-between items-center bg-pink-50 cursor-pointer">
                    <div>
                      <p class="font-bold text-slate-800 text-sm sm:text-base">פדיקור רפואי</p>
                      <p class="text-xs sm:text-sm text-slate-500">45 דקות</p>
                    </div>
                    <span class="font-bold text-pink-600 text-sm sm:text-base">₪150</span>
                  </div>
                </div>
                <button class="w-full mt-4 sm:mt-6 bg-slate-900 text-white font-bold py-3 sm:py-4 rounded-xl text-sm sm:text-base">בחרי שעה לתור</button>
              </div>
            </div>
            <div class="absolute animate-pulse -bottom-4 -right-2 sm:-bottom-6 sm:-right-6 bg-green-100 text-green-700 p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-xl font-bold flex items-center gap-2 text-xs sm:text-sm z-10 scale-90 sm:scale-100 origin-bottom-right">
              <Clock class="w-4 h-4 sm:w-6 sm:h-6 shrink-0 text-green-600" />
              <div class="flex flex-col">
                <span class="truncate">התפנה תור ב-14:00</span>
                <span class="text-[10px] font-medium opacity-80">דניאל שובצה מההמתנה!</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Feature 2: WhatsApp Automations -->
        <div class="flex flex-col lg:flex-row-reverse items-center gap-10 lg:gap-20">
          <div class="flex-1 lg:pl-8 text-center lg:text-right">
            <div class="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 text-green-600 rounded-2xl flex items-center justify-center mb-6 sm:mb-8 mx-auto lg:mx-0 shadow-inner">
              <MessageCircle class="w-7 h-7 sm:w-8 sm:h-8" />
            </div>
            <h3 class="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 leading-tight">תקשורת 360° באוטומציה מלאה (ווטסאפ ו-SMS)</h3>
            <p class="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8">
              הברזות עולות לכם כסף. המערכת שולחת במקומכם אישורי הזמנה מידיים, תזכורות לאישור הגעה (לפני ובבוקר התור), הודעות ביטול, וברכת 'ברוכים הבאים' ללקוחות חדשים. הכל באוטומט, ואתם לא צריכים להקליד מילה.
            </p>
            <div class="bg-white p-5 sm:p-6 rounded-2xl border border-slate-100 shadow-sm text-right">
              <h4 class="font-bold text-slate-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                <Sparkles class="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 shrink-0" />
                התוצאה בשטח:
              </h4>
              <p class="text-sm sm:text-base text-slate-600">הלקוחות שלנו מדווחים על <strong>ירידה דרסטית בכמות ההברזות וביטולי הרגע האחרון.</strong></p>
            </div>
          </div>

          <div class="flex-1 w-full relative mt-6 lg:mt-0">
            <div class="relative bg-slate-100/50 rounded-2xl sm:rounded-[2rem] p-5 sm:p-6 lg:p-10 border border-slate-200">
              <div class="space-y-4 sm:space-y-6 flex flex-col">
                <!-- Automated Confirmation -->
                <div class="bg-white p-3 sm:p-4 rounded-xl sm:rounded-2xl rounded-tr-sm shadow-sm max-w-[90%] sm:max-w-[85%] self-start relative border-r-2 border-green-400">
                  <p class="text-slate-800 text-xs sm:text-base leading-relaxed pb-3">
                    <strong>אישור הזמנה ✅</strong><br/>
                    היי נועה! איזה כיף שהזמנת. התור שלך ל<span class="text-pink-600">שירן ביוטי</span> נקבע בהצלחה למחר ב-10:00.
                  </p>
                  <span class="text-[9px] sm:text-[10px] text-slate-400 absolute bottom-1.5 sm:bottom-2 left-2 sm:left-3">נשלח אוטומטית</span>
                </div>

                <!-- Automated Reminder -->
                <div class="bg-white p-3 sm:p-4 rounded-xl sm:rounded-2xl rounded-tr-sm shadow-sm max-w-[90%] sm:max-w-[85%] self-start relative border-r-2 border-pink-400">
                  <p class="text-slate-800 text-xs sm:text-base leading-relaxed pb-3">
                    <strong>תזכורת הגעה ⏰</strong><br/>
                    רק מוודאת הגעה למחר ב-10:00.<br/>
                    לאישור השיבי 1, לביטול השיבי 2.
                  </p>
                  <span class="text-[9px] sm:text-[10px] text-slate-400 absolute bottom-1.5 sm:bottom-2 left-2 sm:left-3">אתמול 18:00</span>
                </div>

                <!-- Client Reply -->
                <div class="bg-green-100 p-3 sm:p-4 rounded-xl sm:rounded-2xl rounded-tl-sm shadow-sm max-w-[90%] sm:max-w-[85%] self-end relative">
                  <p class="text-slate-800 text-xs sm:text-base pb-3">1. ברור שאני באה!</p>
                  <span class="text-[9px] sm:text-[10px] text-slate-500 absolute bottom-1.5 sm:bottom-2 right-2 sm:right-3">18:05</span>
                  <div class="absolute bottom-1.5 sm:bottom-2 left-2 sm:left-3 flex">
                    <CheckCircle2 class="w-3 h-3 text-blue-500" />
                  </div>
                </div>
              </div>

              <div class="absolute -right-2 w-max sm:-left-4 top-[80%] flex-none transform -translate-y-1/2 bg-white p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-xl flex items-center animate-float gap-3 sm:gap-4 scale-90 sm:scale-100 origin-left z-10">
                <div class="bg-green-50 p-2 sm:p-3 rounded-full shrink-0">
                  <CalendarCheck class="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                </div>
                <div>
                  <p class="text-[10px] sm:text-xs text-slate-500">עדכון חי ביומן</p>
                  <p class="font-bold text-xs sm:text-sm text-slate-900 truncate">הלקוחה אישרה הגעה</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Feature 3: Payments & Invoices -->
        <div class="flex flex-col lg:flex-row items-center gap-10 lg:gap-20">
          <div class="flex-1 lg:pr-8 text-center lg:text-right">
            <div class="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mb-6 sm:mb-8 mx-auto lg:mx-0 shadow-inner">
              <CreditCard class="w-7 h-7 sm:w-8 sm:h-8" />
            </div>
            <h3 class="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 leading-tight">בלי התעסקות עם תשלומים וחשבוניות באוטומט</h3>
            <p class="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8">
              נגמרו הימים של לרדוף אחרי תשלומים או לכתוב קבלות ידניות. גבו מקדמות באשראי, חייבו דמי ביטול, והמערכת <strong>תפיק ותשלח אוטומטית חשבונית/קבלה ללקוחה לוואטסאפ</strong> מיד לאחר התשלום. הכל בשיא המקצועיות, ויש גם ביט!
            </p>
          </div>

          <div class="flex-1 w-full relative mt-6 lg:mt-0">
            <div class="bg-slate-100/80 rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-12 border border-slate-200 shadow-inner relative">
              <div class="bg-white max-w-sm mx-auto rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden border border-slate-100 transform transition-transform hover:-translate-y-2 duration-500">
                <div class="bg-gradient-to-br from-blue-500 to-blue-700 p-6 sm:p-8 text-center text-white relative">
                  <div class="w-12 h-12 sm:w-16 sm:h-16 bg-white/30 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-inner">
                    <Receipt class="w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                  <h4 class="font-medium text-blue-100 text-sm sm:text-base">תשלום התקבל בהצלחה</h4>
                  <p class="text-3xl sm:text-4xl font-black mt-1">₪250.00</p>
                </div>
                <div class="p-4 sm:p-6">
                  <div class="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                    <div class="flex justify-between items-center text-xs sm:text-sm border-b border-slate-50 pb-2 sm:pb-3">
                      <span class="text-slate-500">לקוחה</span>
                      <span class="font-bold text-slate-800">דנה לוי</span>
                    </div>
                    <div class="flex justify-between items-center text-xs sm:text-sm border-b border-slate-50 pb-2 sm:pb-3">
                      <span class="text-slate-500">שירות</span>
                      <span class="font-bold text-slate-800">הרמת ריסים</span>
                    </div>
                  </div>
                  <div class="bg-green-50 text-green-700 p-3 sm:p-4 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold flex items-center justify-center gap-2 border border-green-100 text-center">
                    <CheckCircle2 class="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                    <span>חשבונית נשלחה אוטומטית לוואטסאפ</span>
                  </div>
                </div>
              </div>

              <div class="absolute top-[20%] -left-2 sm:-left-6 bg-white p-2 sm:p-3 rounded-xl sm:rounded-2xl shadow-xl border border-slate-100 flex items-center gap-2 sm:gap-3 animate-float scale-90 sm:scale-100 origin-left z-10" style="animation-duration: 4s">
                <div class="bg-blue-100 p-1.5 sm:p-2 rounded-full text-blue-600 shrink-0">
                  <CreditCard class="w-3 h-3 sm:w-4 sm:h-4" />
                </div>
                <span class="text-[10px] sm:text-sm font-bold text-slate-800 truncate">₪50 מקדמה שולמה</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Feature 4: Online Store & Vouchers -->
        <div class="flex flex-col lg:flex-row-reverse items-center gap-10 lg:gap-20">
          <div class="flex-1 lg:pl-8 text-center lg:text-right">
            <div class="w-14 h-14 sm:w-16 sm:h-16 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center mb-6 sm:mb-8 mx-auto lg:mx-0 shadow-inner">
              <ShoppingBag class="w-7 h-7 sm:w-8 sm:h-8" />
            </div>
            <h3 class="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 leading-tight">הלקוח כבר מגיע אליכם, למה לא להרוויח יותר?</h3>
            <p class="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8">
              למה להסתפק רק בטיפולים? תנו ללקוחות שלכם לפנק את עצמם (או חברים ומשפחה) עם גיפט קארד לעסק שלכם, או את מוצרי הטיפוח שאתם מוכרים, והם כבר יחכו להם בסוף התור. הלקוח יכול להוסיף מוצרים לסל בהזמנת התור, ואם נטש את העגלה - המערכת תשלח לו תזכורת השלמה.
            </p>
          </div>

          <div class="flex-1 w-full relative mt-6 lg:mt-0">
            <div class="bg-gradient-to-tr from-gray-50 to-blue-50 rounded-2xl sm:rounded-[2.5rem] p-5 sm:p-8 lg:p-10 border border-gray-200 relative">
              <div class="flex justify-between items-center mb-4 sm:mb-6 px-1 sm:px-2">
                <h4 class="font-bold text-slate-800 text-lg sm:text-xl">החנות של עידו ברבר</h4>
                <span class="text-orange-600 text-[10px] sm:text-sm font-bold bg-white px-2 sm:px-3 py-1 rounded-full shadow-sm">צפייה בכל המוצרים</span>
              </div>

              <div class="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 relative z-10 pb-4">
                <div class="bg-white p-3 sm:p-4 rounded-2xl sm:rounded-3xl shadow-sm border border-slate-100 hover:shadow-lg transition-shadow group flex flex-col justify-between">
                  <div>
                    <div class="w-full h-24 sm:h-32 bg-orange-50/80 rounded-xl sm:rounded-2xl mb-3 sm:mb-4 flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                      <Gift class="w-8 h-8 sm:w-12 sm:h-12 text-orange-400" />
                    </div>
                    <p class="font-bold text-slate-800 text-[11px] sm:text-sm md:text-base mb-1 leading-tight">גיפט קארד לתספורת</p>
                    <p class="text-[9px] sm:text-xs text-slate-500 mb-3 sm:mb-4 leading-tight">מתנה מושלמת לחבר</p>
                  </div>
                  <div class="flex justify-between items-center mt-auto">
                    <span class="font-black text-sm sm:text-lg text-slate-900">₪300</span>
                    <button class="bg-slate-900 text-white w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:bg-slate-800 shadow-md transform hover:scale-105 transition-transform shrink-0">
                      <span class="text-base sm:text-xl leading-none mb-0.5">+</span>
                    </button>
                  </div>
                </div>

                <div class="bg-white p-3 sm:p-4 rounded-2xl sm:rounded-3xl shadow-sm border border-slate-100 hover:shadow-lg transition-shadow group flex flex-col justify-between">
                  <div>
                    <div class="w-full h-24 sm:h-32 bg-blue-50/80 rounded-xl sm:rounded-2xl mb-3 sm:mb-4 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                      <div class="w-6 h-10 sm:w-10 sm:h-16 bg-blue-200 rounded-md sm:rounded-lg border-2 border-blue-300 relative">
                        <div class="absolute -top-2 sm:-top-3 left-1/2 transform -translate-x-1/2 w-3 sm:w-5 h-2 sm:h-3 bg-blue-400 rounded-t-sm"></div>
                      </div>
                    </div>
                    <p class="font-bold text-slate-800 text-[11px] sm:text-sm md:text-base mb-1 leading-tight">קרם שיער</p>
                    <p class="text-[9px] sm:text-xs text-slate-500 mb-3 sm:mb-4 leading-tight">150 מ"ל, מסייע בשיקום</p>
                  </div>
                  <div class="flex justify-between items-center mt-auto">
                    <span class="font-black text-sm sm:text-lg text-slate-900">₪180</span>
                    <button class="bg-slate-900 text-white w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center hover:bg-slate-800 shadow-md transform hover:scale-105 transition-transform shrink-0">
                      <span class="text-base sm:text-xl leading-none mb-0.5">+</span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="absolute -bottom-4 sm:-bottom-6 sm:left-1/2 transform md:-translate-x-1/2 animate-pulse bg-slate-900 text-white px-4 py-3 sm:px-6 sm:py-4 rounded-full shadow-2xl flex items-center justify-center gap-2 sm:gap-3 w-[90%] sm:w-max z-20">
                <ShoppingBag class="w-4 h-4 sm:w-5 sm:h-5 text-orange-400 shrink-0" />
                <span class="font-bold text-[10px] sm:text-sm truncate">גיפט קארד נוסף לעגלה! תהנו.</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Feature 5: Loyalty, Follow-up & Marketing -->
        <div class="flex flex-col lg:flex-row items-center gap-10 lg:gap-20">
          <div class="flex-1 lg:pr-8 text-center lg:text-right">
            <div class="w-14 h-14 sm:w-16 sm:h-16 bg-rose-100 text-rose-600 rounded-2xl flex items-center justify-center mb-6 sm:mb-8 mx-auto lg:mx-0 shadow-inner">
              <Star class="w-7 h-7 sm:w-8 sm:h-8" />
            </div>
            <h3 class="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 leading-tight">לקוחות שחוזרים שוב ושוב: מועדון, משוב ופולו-אפ חכם</h3>
            <p class="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8">
              למה לחכות שהלקוחות יזכרו בכם? המערכת שולחת באוטומט הכל: הודעת יום הולדת עם מבצע, בקשת משוב ודירוג יום אחרי הטיפול, או הודעת 'התגעגענו' ללקוחות שלא ביקרו תקופה. תוסיפו לזה כרטיסיית מועדון דיגיטלית - והלקוחות תמיד שלכם.
            </p>
          </div>

          <div class="flex-1 w-full relative mt-6 lg:mt-0">
            <div class="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10 border border-rose-100 relative shadow-inner">
              <!-- Mockup: Feedback Request -->
              <div class="bg-white rounded-2xl rounded-tr-sm shadow-md p-4 w-[90%] sm:w-[85%] self-start relative transform transition-transform hover:-translate-y-1 mb-4 z-10 border-l-4 border-yellow-400">
                <p class="text-slate-800 text-xs sm:text-sm leading-relaxed pb-3">
                  <strong>איך היה הטיפול שלך? 💅</strong><br/>
                  נשמח לשמוע איך התרשמת מהטיפול אתמול אצלנו. הדירוג שלך עוזר לנו להשתפר!
                </p>
                <div class="flex gap-1 pb-4">
                  <Star v-for="i in 5" :key="i" class="w-4 h-4 text-slate-300 hover:text-yellow-400 cursor-pointer transition-colors" />
                </div>
                <span class="text-[9px] sm:text-[10px] text-slate-400 absolute bottom-1.5 sm:bottom-2 left-2 sm:left-3">אוטומציה: יום אחרי תור</span>
              </div>

              <!-- Mockup: Loyalty Card -->
              <div class="bg-white rounded-2xl shadow-md p-5 mb-4 transform transition-transform hover:-translate-y-1 opacity-90 scale-95">
                <div class="flex justify-between items-center mb-4">
                  <div class="flex items-center gap-3">
                    <div class="bg-rose-100 p-2 rounded-full"><Star class="w-5 h-5 text-rose-500" /></div>
                    <div>
                      <h4 class="font-bold text-slate-800 text-sm">מועדון הלקוחות של שירן</h4>
                      <p class="text-xs text-slate-500">נועה לוי - חברת מועדון</p>
                    </div>
                  </div>
                  <span class="bg-rose-50 text-rose-600 font-bold text-xs px-3 py-1 rounded-full">4/5</span>
                </div>

                <div class="flex justify-between items-center gap-2 mb-2">
                  <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-rose-500 text-white flex items-center justify-center shrink-0"><CheckCircle2 class="w-4 h-4 sm:w-5 sm:h-5" /></div>
                  <div class="h-1 flex-1 bg-rose-200 rounded-full min-w-[8px]"></div>
                  <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-rose-500 text-white flex items-center justify-center shrink-0"><CheckCircle2 class="w-4 h-4 sm:w-5 sm:h-5" /></div>
                  <div class="h-1 flex-1 bg-rose-200 rounded-full min-w-[8px]"></div>
                  <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-slate-100 text-slate-400 border-2 border-dashed border-slate-300 flex items-center justify-center shrink-0"><Gift class="w-4 h-4 sm:w-5 sm:h-5" /></div>
                </div>
              </div>

              <div class="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-slate-900 text-white p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-xl font-bold flex items-center gap-2 text-xs sm:text-sm z-20 scale-90 sm:scale-100 origin-bottom-left">
                <Megaphone class="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 shrink-0" />
                <span class="truncate">הודעת מבצע נשלחה ל-150 לקוחות!</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Feature 6: Digital Forms -->
        <div class="flex flex-col lg:flex-row items-center gap-10 lg:gap-20">
          <!-- Mockup Side -->
          <div class="flex-1 w-full relative perspective-1000 mt-6 lg:mt-0">
            <div class="relative bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl shadow-cyan-100/50 border border-cyan-100 overflow-hidden transform transition-transform hover:-translate-y-2 duration-500 p-4 sm:p-6 lg:p-8">
              <!-- Mockup Form -->
              <div class="bg-white rounded-[1.5rem] shadow-sm border border-slate-100 overflow-hidden max-w-[260px] sm:max-w-[300px] mx-auto">
                <div class="bg-slate-900 text-white p-4 text-center">
                  <h4 class="font-bold text-sm sm:text-base">שירן ביוטי - קליניקה</h4>
                  <p class="text-[10px] sm:text-xs text-slate-300">הצהרת בריאות והסכמה לטיפול פנים</p>
                </div>
                <div class="p-4 sm:p-5 space-y-4">
                  <div class="space-y-2">
                    <p class="text-xs sm:text-sm font-bold text-slate-800">האם ידוע לך על אלרגיות כלשהן?</p>
                    <div class="flex flex-col gap-2">
                      <label class="flex items-center gap-2 text-xs sm:text-sm text-slate-600 cursor-pointer">
                        <input type="radio" name="allergy" class="w-3 h-3 text-cyan-500 focus:ring-cyan-500" checked />
                        <span>לא, איני סובלת מאלרגיות.</span>
                      </label>
                      <label class="flex items-center gap-2 text-xs sm:text-sm text-slate-600 cursor-pointer">
                        <input type="radio" name="allergy" class="w-3 h-3 text-cyan-500 focus:ring-cyan-500" />
                        <span>כן (אפרט מטה)</span>
                      </label>
                    </div>
                  </div>

                  <div class="pt-3 border-t border-slate-100">
                    <label class="flex items-start gap-2 text-xs sm:text-sm text-slate-600 cursor-pointer">
                      <input type="checkbox" class="mt-1 w-3 h-3 text-cyan-500 rounded focus:ring-cyan-500" checked />
                      <span class="leading-tight">אני מאשרת שקראתי והבנתי את ההנחיות לפני הטיפול ואני מסכימה לתנאים.</span>
                    </label>
                  </div>

                  <div class="pt-2">
                    <p class="text-[10px] sm:text-xs text-slate-500 mb-1">חתימת הלקוחה:</p>
                    <div class="h-16 bg-slate-50 border border-dashed border-slate-300 rounded-xl flex items-center justify-center relative">
                      <svg viewBox="0 0 100 30" class="w-20 h-8 stroke-cyan-800 stroke-2 fill-none" style="stroke-linecap: round; stroke-linejoin: round;">
                        <path d="M10,20 Q20,5 30,15 T50,25 Q60,10 70,20 T90,15" />
                      </svg>
                    </div>
                  </div>

                  <button class="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-2.5 rounded-xl text-xs sm:text-sm transition-colors shadow-sm">
                    חתימה ושליחה
                  </button>
                </div>
              </div>
            </div>
            <div class="absolute -bottom-4 -right-2 sm:-bottom-6 sm:-right-6 bg-slate-900 text-white p-3 sm:p-4 rounded-xl sm:rounded-2xl z-50 shadow-xl font-bold flex items-center gap-2 text-xs sm:text-sm scale-90 sm:scale-100 origin-bottom-right border border-slate-700">
              <CheckSquare class="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 shrink-0" />
              <div class="flex flex-col whitespace-nowrap">
                <span class="text-[10px] text-slate-400 leading-none mb-0.5">נחתם דיגיטלית</span>
                <span>הטופס של יעל נשמר בתיק!</span>
              </div>
            </div>
          </div>

          <!-- Text Side -->
          <div class="flex-1 lg:pr-8 text-center lg:text-right">
            <div class="w-14 h-14 sm:w-16 sm:h-16 bg-cyan-100 text-cyan-600 rounded-2xl flex items-center justify-center mb-6 sm:mb-8 mx-auto lg:mx-0 shadow-inner">
              <FileText class="w-7 h-7 sm:w-8 sm:h-8" />
            </div>
            <h3 class="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-4 sm:mb-6 leading-tight">טפסים ואישורים דיגיטליים בקליק</h3>
            <p class="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 sm:mb-8">
              הלקוחה צריכה לחתום על הצהרת בריאות או אישור טיפול? לא צריך יותר להדפיס ולתייק ניירות. ה-AI ייצר עבורך את הטופס בשניות, והמערכת תשלח אותו אוטומטית לוואטסאפ של הלקוחה לפני התור.
            </p>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<style scoped>
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}
.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style>
